import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api, API, formatINR, useCart, useAuth, formatISTDate, formatISTDdMmYyyyForFilename } from '../lib/store';
import axios from 'axios';
import { formatPdfDescription, formatPdfUnit, fmtPctDiscount, pdfStyleNetRate, specialDiscountLabel, roundINR, formatCascadingLabel } from '../lib/pdfFormat';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, Download, User, Phone, MapPin, Share2, Copy, MessageCircle, Mail, RefreshCw, Pencil, FileText, Building2, Printer, GitCompare, FileStack, Trash2, AlertTriangle, Truck, CheckCircle, MoreHorizontal, Calendar, X, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

// Adhesives store the weight/volume in `thickness` (e.g., "90 GMS", "500 ML").
// Use that as the customer-facing size, falling back to item.size for all
// other categories.
const renderItemSize = (item) => {
  if (item.main_category === 'Adhesives' && item.thickness) return item.thickness;
  return item.size || item.thickness || '-';
};

// Format a percent discount: 40.0 → "40%", 12.5 → "12.5%", 0 → ""
// (Imported from pdfFormat.js; kept as fallback name for the original component.)

export default function QuotationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Unified back behaviour — use browser-native history via
  // navigate(-1) so the button lands the user on whichever page they
  // actually came from (History, Today tab, Staff tab, Dashboard,
  // comparison sibling, Customer profile, WhatsApp deep-link when
  // there's an in-app referrer, etc.). Previous iter-118 / iter-129
  // per-source `?from=…` branches removed — they were per-caller
  // patches that native back subsumes. QuotationDetailPage is never
  // an initial-load-only route (unlike /qr/:id), so navigate(-1)
  // always has a meaningful destination.
  const goBack = () => navigate(-1);
  const { loadQuotationForEdit } = useCart();
  const { user, can } = useAuth();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteComparisonDialog, setShowDeleteComparisonDialog] = useState(false);
  const [deletingComparison, setDeletingComparison] = useState(false);
  // Comparison group members (id, quotation_number,
  // option_number, grand_total) for banner sizing and per-option
  // "View" / "Keep" buttons. `null` until fetched; empty when the
  // current quotation isn't a comparison.
  const [groupMembers, setGroupMembers] = useState(null);

  useEffect(() => {
    api.getQuotation(id).then(r => setQuotation(r.data)).catch(() => toast.error('Quotation not found')).finally(() => setLoading(false));
  }, [id]);

  // Fetch group members whenever the quotation is confirmed
  // to be a comparison. Runs cheap — a single indexed query — and
  // updates on every navigation between group members.
  useEffect(() => {
    if (!quotation?.is_comparison) { setGroupMembers([]); return undefined; }
    let cancelled = false;
    api.getComparisonGroup(id).then((r) => {
      if (!cancelled) setGroupMembers(r.data?.members || []);
    }).catch(() => { if (!cancelled) setGroupMembers([]); });
    return () => { cancelled = true; };
  }, [id, quotation?.is_comparison]);

  const handleDownload = async () => {
    try {
      const res = await api.downloadPdf(id, false);  // Customer PDF (hide brands)
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const customerName = quotation.customer?.name?.replace(/\s+/g, '_') || 'Customer';
      const dateStr = formatISTDdMmYyyyForFilename(quotation.updated_at || quotation.created_at);
      a.download = `Quotation_for_${customerName}_${dateStr}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Customer PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download');
    }
  };

  const handleInternalDownload = async () => {
    try {
      const res = await api.downloadInternalPdf(id);  // Internal PDF (show brands)
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const customerName = quotation.customer?.name?.replace(/\s+/g, '_') || 'Customer';
      const dateStr = formatISTDdMmYyyyForFilename(quotation.updated_at || quotation.created_at);
      a.download = `Quotation_INTERNAL_${customerName}_${dateStr}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Internal PDF downloaded (with brand names)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download');
    }
  };

  const handlePrint = async () => {
    try {
      toast.info('Preparing PDF for printing...');
      const res = await api.downloadPdf(id, false);  // Customer PDF
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open PDF in new window and trigger print
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
        toast.success('Print dialog opened');
      } else {
        // Fallback: Download if popup blocked
        const a = document.createElement('a');
        a.href = url;
        const customerName = quotation.customer?.name?.replace(/\s+/g, '_') || 'Customer';
        const dateStr = formatISTDdMmYyyyForFilename(quotation.updated_at || quotation.created_at);
        a.download = `Quotation_for_${customerName}_${dateStr}.pdf`;
        a.click();
        toast.info('Please open the downloaded PDF to print');
      }
      
      // Clean up URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare for printing');
    }
  };

  const handleWhatsApp = async () => {
    const customer = quotation.customer || {};
    const mobile = customer.mobile ? customer.mobile.replace(/\D/g, '') : '';
    const phone = mobile.startsWith('91') ? mobile : `91${mobile}`;
    
    // First download the PDF
    toast.info('Downloading PDF...');
    let pdfDownloaded = false;
    try {
      const response = await api.downloadQuotationPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quotation.quotation_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
      pdfDownloaded = true;
    } catch (err) {
      logger.error('PDF download failed:', err);
      toast.error(err.response?.data?.detail || 'PDF download failed');
    }

    if (!pdfDownloaded) return;
    
    // Then open WhatsApp with personalized message
    const customerName = customer.name || 'Sir/Madam';
    const text = encodeURIComponent(
      `Dear ${customerName},\n\nPlease find the attached quotation.\n\nRegards,\nTeam HEDA PLYWOODS`
    );
    const whatsappUrl = mobile
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp - Please attach the downloaded PDF');
  };

  const handleEmail = () => {
    const customer = quotation.customer || {};
    const subject = encodeURIComponent(`Quotation - HEDA PLYWOODS`);
    const body = encodeURIComponent(
      `Dear ${customer.name || 'Customer'},\n\nPlease find the attached quotation.\n\nThank you,\nHEDA PLYWOODS`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleDuplicate = async () => {
    try {
      const res = await api.duplicateQuotation(id);
      toast.success(`Quotation duplicated: ${res.data.quotation_number}`);
      navigate(`/quotations/${res.data.id}`);
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleEdit = () => {
    loadQuotationForEdit(quotation);
    navigate(`/cart?edit=${id}&subject=${encodeURIComponent(quotation.subject || '')}&overallDiscType=${quotation.overall_discount_type || 'percent'}&overallDiscValue=${quotation.overall_discount_value || 0}`);
  };

  const handleCreateComparison = async () => {
    try {
      const isAdding = quotation.is_comparison;
      toast.info(isAdding ? 'Adding a new option to this comparison…' : 'Creating comparison quotation...');
      const res = await api.createComparisonQuotation(id);
      const newOptNum = res.data?.option_number;
      toast.success(
        isAdding
          ? `Option ${newOptNum || '?'} added. You can now modify its products.`
          : 'Comparison quotation created! You can now modify products for Option 2.'
      );
      // Load the new quotation for editing in comparison mode
      const newQuotation = await api.getQuotation(res.data.id);
      loadQuotationForEdit(newQuotation.data);
      // parentId — for legacy pair backward-compat with the Cart page,
      // pass Option 1's id when we know it; else the source id.
      const parentId = res.data?.comparison_parent_id || id;
      navigate(`/cart?edit=${res.data.id}&comparison=true&parentId=${parentId}&subject=${encodeURIComponent(quotation.subject || '')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create comparison');
    }
  };

  const handleDownloadComparisonPdf = async () => {
    try {
      toast.info('Generating comparison PDF...');
      const res = await api.downloadComparisonPdf(id, false);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const customerName = quotation.customer?.name?.replace(/\s+/g, '_') || 'Customer';
      const dateStr = formatISTDdMmYyyyForFilename(quotation.updated_at || quotation.created_at);
      a.download = `Comparison_Quotation_${customerName}_${dateStr}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Comparison PDF downloaded!');
    } catch (err) {
      // When responseType='blob', a 500 error body comes back as a Blob, not
      // JSON — so err.response?.data?.detail is always undefined and the user
      // sees a generic toast. Decode the blob to surface the actual backend
      // message (and log full error to the console for production debugging).
      let detail = '';
      try {
        const blob = err.response?.data;
        if (blob && typeof blob.text === 'function') {
          const text = await blob.text();
          try {
            detail = JSON.parse(text).detail || text;
          } catch (_) {
            detail = text;
          }
        }
      } catch (_) { /* ignore decode errors */ }
      detail = detail || err.response?.data?.detail || err.message || 'Unknown error';
      logger.error('Comparison PDF download failed', err.response?.status, detail);
      toast.error(`Comparison PDF failed: ${String(detail).slice(0, 200)}`);
    }
  };

  const handleDeleteComparison = async (keepOptionOrId) => {
    setDeletingComparison(true);
    try {
      const res = await api.deleteComparison(id, keepOptionOrId);
      // Prefer a friendly label — "Option N" — over the raw id when the
      // caller passed an id. Look up the option_number from the members
      // we already have.
      const keptMember = (groupMembers || []).find(m => m.id === res.data?.kept_quotation_id);
      const label = keptMember
        ? `Option ${keptMember.option_number}`
        : (typeof keepOptionOrId === 'string' && keepOptionOrId.startsWith('option')
          ? `Option ${keepOptionOrId.slice(6)}`
          : 'the selected option');
      toast.success(`Comparison deleted. Kept ${label}.`);
      setShowDeleteComparisonDialog(false);

      const keptId = res.data.kept_quotation_id;
      if (keptId === id) {
        // If staying on the same quotation, re-fetch to update state
        const refreshed = await api.getQuotation(keptId);
        setQuotation(refreshed.data);
        setGroupMembers([]);
      } else {
        // Navigate to the other quotation
        navigate(`/quotations/${keptId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete comparison');
    } finally {
      setDeletingComparison(false);
    }
  };

  const handleMarkDelivered = async () => {
    if (!window.confirm('Mark this quotation as delivered? This will deduct stock for Plywood, Blockboard, Doors, MDF, and WPC/PVC items not yet delivered.')) return;
    try {
      const res = await api.markDelivered(id);
      toast.success('Quotation marked as delivered. Stock updated.');
      // Refresh quotation data
      const refreshed = await api.getQuotation(id);
      setQuotation(refreshed.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as delivered');
    }
  };

  const handleUnmarkDelivered = async () => {
    if (!window.confirm('Revert delivery status? This will add stock back for the items.')) return;
    try {
      await api.unmarkDelivered(id);
      toast.success('Delivery status reverted. Stock restored.');
      const refreshed = await api.getQuotation(id);
      setQuotation(refreshed.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revert');
    }
  };

  // Same role permissions the backend checks.
  const canMarkDelivered = can('delivery.mark_quotation');
  const canUndoDelivered = can('delivery.unmark_quotation');

  // Follow-up date — additive PATCH. Permissions handled by the
  // backend (creator / salesperson / admin+manager+dispatcher); a
  // 403 surfaces as a toast without breaking the page state.
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const saveFollowUp = async (nextDate) => {
    setSavingFollowUp(true);
    try {
      const token = localStorage.getItem('heda_token') || '';
      const res = await axios.patch(
        `${API}/quotations/${id}/follow-up`,
        { follow_up_date: nextDate || null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setQuotation((q) => q ? { ...q, follow_up_date: res.data.follow_up_date } : q);
      toast.success(nextDate ? 'Follow-up date set' : 'Follow-up cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update follow-up');
    } finally {
      setSavingFollowUp(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center gap-2 py-20 text-sm text-heda-mute"><Loader2 className="h-5 w-5 animate-spin text-heda-gold" /> Loading...</div>;
  if (!quotation) return <div className="flex items-center justify-center py-20 text-red-700">Quotation not found</div>;

  const formatDate = (iso) => formatISTDate(iso);
  // Delivered via Mark Delivered or by delivering every item on the Delivery page.
  const isDelivered = !!quotation.is_delivered || quotation.delivery_status === 'delivered';

  return (
    <div className="max-w-4xl mx-auto" data-testid="quotation-detail-page">
      <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={goBack} className="rounded-xl h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3" data-testid="back-to-quotations-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate font-mono text-[18px] font-bold text-heda-ink sm:text-[20px]" data-testid="quotation-number">{quotation.quotation_number}</h1>
              {quotation.is_comparison && (
                <Badge className="rounded-full bg-heda-gold text-white text-[10px]" data-testid="comparison-badge">
                  {quotation.comparison_label || (quotation.comparison_parent_id ? 'Option 2' : 'Option 1')}
                </Badge>
              )}
              {isDelivered && (
                <Badge className="rounded-full bg-emerald-700 text-white text-[10px]" data-testid="delivered-badge">
                  ✓ Delivered
                </Badge>
              )}
              {quotation.follow_up_date && (
                <Badge className="rounded-full bg-heda-ink text-white text-[10px] flex items-center gap-1" data-testid="followup-badge">
                  <Calendar className="w-3 h-3" /> Follow-up {formatISTDate(quotation.follow_up_date)}
                </Badge>
              )}
            </div>
            <p className="text-[10px] sm:text-xs text-heda-mute truncate">
              {formatDate(quotation.updated_at || quotation.created_at)}
              {quotation.updated_at && quotation.created_at && quotation.updated_at !== quotation.created_at && (
                <span className="ml-1 text-heda-mute" title={`Originally created ${formatDate(quotation.created_at)}`}> (edited)</span>
              )}
              {' | '}{quotation.created_by_name}
            </p>
            {/* Follow-up date control — compact inline picker sitting
                right under the title so it's discoverable without a
                menu. Backend enforces the permission set; a 403 lands
                as a toast without breaking the rest of the page. */}
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid="followup-control">
              <span className="text-[10px] uppercase tracking-wide text-heda-mute">Follow-up:</span>
              <input
                type="date"
                value={quotation.follow_up_date || ''}
                onChange={(e) => saveFollowUp(e.target.value)}
                disabled={savingFollowUp}
                className="h-7 text-xs px-2 border border-heda-line rounded-lg bg-white disabled:opacity-50 font-mono"
                data-testid="followup-input"
              />
              {quotation.follow_up_date && (
                <button
                  type="button"
                  onClick={() => saveFollowUp(null)}
                  disabled={savingFollowUp}
                  className="h-7 w-7 flex items-center justify-center text-heda-mute hover:text-red-700 rounded-lg"
                  title="Clear follow-up"
                  data-testid="followup-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Info Banner — "Option X of N" + one
          "View Option Y" button per OTHER group member (1 for a pair,
          2 for a 3-option group). Falls back to the legacy inference
          when the group-members query hasn't returned yet (pair-only,
          keeping the pre-iter-131 look). */}
      {quotation.is_comparison && (() => {
        const members = groupMembers || [];
        const groupSize = members.length || 2;
        const currentOpt = (() => {
          if (typeof quotation.option_number === 'number') return quotation.option_number;
          // Legacy fallback while members are still loading.
          return quotation.comparison_parent_id ? 2 : 1;
        })();
        const otherMembers = members.filter(m => m.id !== id);
        // Legacy 2-pair fallback view — matches the pre-iter-131 UI
        // exactly (single "View Option X" button, N=2). Only used when
        // the /comparison-group fetch hasn't returned yet.
        const legacyLinkedId = quotation.comparison_parent_id || quotation.comparison_child_id;
        return (
          <div className="bg-heda-gold-tint border border-heda-gold/30 rounded-xl p-3 mb-4 flex items-center justify-between flex-wrap gap-2" data-testid="comparison-info-banner">
            <div className="flex items-center gap-2">
              <GitCompare className="w-4 h-4 text-heda-gold" />
              <span className="text-sm text-heda-gold">
                This is <strong>Option {currentOpt} of {groupSize}</strong> of a comparison quotation
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {otherMembers.length > 0 ? (
                otherMembers.map((m) => (
                  <Button
                    key={m.id}
                    onClick={() => navigate(`/quotations/${m.id}`)}
                    variant="outline"
                    size="sm"
                    className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                    data-testid={`view-option-${m.option_number}-btn`}
                  >
                    View Option {m.option_number}
                  </Button>
                ))
              ) : legacyLinkedId && (
                <Button
                  onClick={() => navigate(`/quotations/${legacyLinkedId}`)}
                  variant="outline"
                  size="sm"
                  className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  data-testid="view-linked-quotation-btn"
                >
                  View {quotation.comparison_parent_id ? 'Option 1' : 'Option 2'}
                </Button>
              )}
              {/* Add-option button surfaces here for members
                  of a group of size <3. UI cap at 3; backend allows
                  more but we choose not to expose it. */}
              {groupSize < 3 && (
                <Button
                  onClick={handleCreateComparison}
                  variant="outline"
                  size="sm"
                  className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  data-testid="add-option-btn"
                >
                  <GitCompare className="w-3 h-3 mr-1" /> Add Option {groupSize + 1}
                </Button>
              )}
              <Button
                onClick={handleDownloadComparisonPdf}
                size="sm"
                className="rounded-xl h-7 text-xs bg-white border bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                variant="outline"
                data-testid="download-comparison-pdf-btn"
              >
                <FileStack className="w-3 h-3 mr-1" /> Comparison PDF
              </Button>
              <Button
                onClick={() => setShowDeleteComparisonDialog(true)}
                variant="outline"
                size="sm"
                className="rounded-xl h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                data-testid="delete-comparison-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete Comparison
              </Button>
            </div>
          </div>
        );
      })()}

      {/* Share Actions - Scrollable on mobile */}
      <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-hide" data-testid="share-actions">
        <Button onClick={handleDownload} className="rounded-xl flex-shrink-0 bg-heda-ink hover:bg-black text-white text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="download-pdf-btn">
          <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> <span className="hidden xs:inline">Download</span> PDF
        </Button>
        <Button onClick={handleInternalDownload} variant="outline" className="rounded-xl flex-shrink-0 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="download-internal-pdf-btn">
          <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Internal
        </Button>
        <Button onClick={handleEdit} variant="outline" className="rounded-xl flex-shrink-0 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="edit-quotation-btn">
          <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Edit
        </Button>
        {/* Create Comparison button - only show if not already a comparison quotation */}
        {!quotation.is_comparison && (
          <Button onClick={handleCreateComparison} variant="outline" className="rounded-xl flex-shrink-0 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="create-comparison-btn">
            <GitCompare className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Compare
          </Button>
        )}
        <Button onClick={handleWhatsApp} variant="outline" className="rounded-xl flex-shrink-0 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="share-whatsapp-btn">
          <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-emerald-700" /> WhatsApp
        </Button>
        {/* Email / Print / Repeat consolidated into a single "More"
            dropdown at the end of the row to keep the header calm. All
            three onClick handlers are unchanged. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-xl flex-shrink-0 bg-white border-heda-line text-heda-sub hover:bg-white hover:border-heda-gold hover:text-heda-gold text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="quotation-more-btn">
              <MoreHorizontal className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> More
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40" data-testid="quotation-more-menu">
            <DropdownMenuItem onClick={handleEmail} data-testid="share-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Email
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handlePrint} data-testid="print-quotation-btn">
              <Printer className="w-4 h-4 mr-2" /> Print
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDuplicate} data-testid="duplicate-quotation-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Repeat
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Mark as Delivered - Admin/Manager only */}
        {canMarkDelivered && !isDelivered && (
          <Button onClick={handleMarkDelivered} variant="outline" className="rounded-xl flex-shrink-0 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="mark-delivered-btn">
            <Truck className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Mark Delivered
          </Button>
        )}
        {/* Undo only reverts a Mark Delivered; item-by-item deliveries
            are reverted on the Delivery page. */}
        {canUndoDelivered && quotation.is_delivered && quotation.delivered_via !== 'items' && (
          <Button onClick={handleUnmarkDelivered} className="rounded-xl flex-shrink-0 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] sm:text-xs uppercase tracking-wider h-8 sm:h-9 px-2 sm:px-3" data-testid="unmark-delivered-btn">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Delivered ✓
          </Button>
        )}
      </div>

      {/* Customer Info */}
      <Card className="rounded-2xl bg-white shadow-none border-heda-line mb-4" data-testid="customer-info-card">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <button 
              onClick={() => quotation.customer?.mobile && navigate(`/customers/${encodeURIComponent(quotation.customer.mobile)}`)}
              className="flex items-center gap-2 hover:text-heda-olive-dark transition-colors"
              title="View customer history"
            >
              <User className="w-4 h-4 text-heda-mute" />
              <span className="font-semibold underline decoration-dotted">{quotation.customer?.name}</span>
            </button>
            {quotation.customer?.mobile && (
              <button 
                onClick={() => navigate(`/customers/${encodeURIComponent(quotation.customer.mobile)}`)}
                className="flex items-center gap-2 hover:text-heda-olive-dark transition-colors"
                title="View customer history"
              >
                <Phone className="w-4 h-4 text-heda-mute" />
                <span className="text-sm underline decoration-dotted">{quotation.customer.mobile}</span>
              </button>
            )}
            {quotation.customer?.address && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-heda-mute" />
                <span className="text-sm">{quotation.customer.address}</span>
              </div>
            )}
            {quotation.customer?.gst_no && (
              <Badge variant="outline" className="rounded-full text-xs">GST: {quotation.customer.gst_no}</Badge>
            )}
            {quotation.customer?.project_name && (
              <Badge variant="outline" className="rounded-full text-xs">Project: {quotation.customer.project_name}</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Items - Grouped by Section then Category (same as PDF) */}
      <div className="bg-white border border-heda-line rounded-xl mb-4">
        <div className="overflow-x-auto">
        {(() => {
          // Check if any item has a discount
          const hasAnyDiscount = quotation.items?.some(item => 
            (item.cascading_discount && item.cascading_discount.trim()) || 
            (item.discount_value && item.discount_value > 0)
          );

          // Check if any item has a per-line Special Discount
          // so the SPL DISC column is rendered ONLY on quotations that
          // actually carry one — same rule the PDF uses (existing
          // quotations without the field look identical to before).
          const hasAnySpecialDiscount = quotation.items?.some(item =>
            Number(item.special_discount || 0) > 0
          );

          // Display fix — mirrors backend
          // services/pdf/item_rows.py::_special_discount_factor exactly.
          // Ratio-based so it stays correct for per-sqft and per-running-feet
          // rate types where NET RATE is a per-unit price.
          const specialDiscountFactor = (item) => {
            const spl = Number(item.special_discount || 0);
            if (spl <= 0) return 1.0;
            const nr = Number(item.net_rate || 0);
            const qty = Number(item.quantity || 0);
            const gross = nr * qty;
            if (gross <= 0) return 1.0;
            const stype = ((item.special_discount_type || 'amount') + '').toLowerCase();
            const reduction = stype === 'percent'
              ? gross * (Math.min(100, spl) / 100)
              : spl;
            return Math.max(0, (gross - reduction) / gross);
          };

          // Cell renderer for the read-only SPL DISC column
          // — shows ONLY what the user entered (percent → "5%",
          // amount → "Rs. 570"). The rupee reduction is visible in the
          // reduced NET RATE column. Zero/blank shows an em-dash.
          const renderSplDisc = (item) => {
            const val = Number(item.special_discount || 0);
            if (val <= 0) {
              return <td className="py-2 px-3 text-right text-xs text-heda-mute">—</td>;
            }
            const type = ((item.special_discount_type || 'amount') + '').toLowerCase();
            const text = type === 'percent'
              ? `${val}%`
              : `Rs. ${Math.round(val).toLocaleString('en-IN')}`;
            return (
              <td className="py-2 px-3 text-right font-mono-data text-xs font-bold text-red-700" data-testid="detail-spl-disc-cell">
                {text}
              </td>
            );
          };

          // Pure refactor — one helper replaces four
          // near-duplicate row-render blocks (sections × brand, sections
          // × plain, no-sections × brand, no-sections × plain). Behaviour
          // and visual output are byte-identical to before. The only
          // per-caller distinction was the description formatter's
          // `showBrandDescriptionItems` flag (true for the brand-grouped
          // variants) plus a slightly different <td className> on the
          // description cell — both threaded through via `variant`.
          //   variant='brand' → brand_description-grouped: description
          //     collapses to thickness only, no max-width truncation.
          //   variant='plain' → default: full description, break-words
          //     with responsive max-width.
          const renderItemRow = (item, idx, variant = 'plain') => {
            const discountDisplay = (item.cascading_discount
              ? formatCascadingLabel(item.cascading_discount, item.cascading_discount_type)
              : '') || (item.discount_value > 0
                ? (item.discount_type === 'percent' ? fmtPctDiscount(item.discount_value) : formatINR(item.discount_value))
                : 'NET');
            const isBrand = variant === 'brand';
            const displayDesc = formatPdfDescription(item, {
              hideBrands: true,
              showBrandDescriptionItems: isBrand && !!item.brand_description,
            });
            const descClass = isBrand
              ? 'py-2 px-3 text-xs font-medium'
              : 'py-2 px-3 text-xs font-medium break-words max-w-[200px] sm:max-w-none';
            return (
              <tr key={item.id || idx} className="border-t border-heda-line">
                <td className="py-2 px-3 text-xs text-heda-mute">{idx}</td>
                <td className={descClass}>{displayDesc}</td>
                <td className="py-2 px-3 font-mono-data text-xs">{renderItemSize(item)}</td>
                <td className="py-2 px-3 text-center font-mono-data text-xs">{item.quantity}</td>
                <td className="py-2 px-3 text-right font-mono-data text-xs">{formatINR(item.rate)}</td>
                <td className="py-2 px-3 text-center text-xs">{formatPdfUnit(item)}</td>
                {hasAnyDiscount && (
                  <td className="py-2 px-3 text-center text-xs text-heda-mute bg-heda-gold-tint">
                    {discountDisplay}
                  </td>
                )}
                <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold">{formatINR(pdfStyleNetRate(item) * specialDiscountFactor(item))}</td>
                {hasAnySpecialDiscount && renderSplDisc(item)}
                <td className="py-2 px-3 text-right font-mono-data text-xs font-bold text-heda-olive-dark">{formatINR(roundINR(item.amount))}</td>
              </tr>
            );
          };
          
          return (
            <table className="w-full text-sm min-w-[640px]" data-testid="quotation-items-table">
              <thead className="heda-thead">
                <tr>
                  <th className="text-left py-2 px-3 text-xs uppercase font-semibold">SR</th>
                  <th className="text-left py-2 px-3 text-xs uppercase font-semibold">Description</th>
                  <th className="text-left py-2 px-3 text-xs uppercase font-semibold">Size</th>
                  <th className="text-center py-2 px-3 text-xs uppercase font-semibold">Qty</th>
                  <th className="text-right py-2 px-3 text-xs uppercase font-semibold">Rate</th>
                  <th className="text-center py-2 px-3 text-xs uppercase font-semibold">Unit</th>
                  {hasAnyDiscount && (
                    <th className="text-center py-2 px-3 text-xs uppercase font-semibold text-heda-gold">Disc</th>
                  )}
                  <th className="text-right py-2 px-3 text-xs uppercase font-semibold">Net Rate</th>
                  {hasAnySpecialDiscount && (
                    <th className="text-right py-2 px-3 text-xs uppercase font-semibold text-red-700" data-testid="detail-spl-disc-header">Spl Disc</th>
                  )}
                  <th className="text-right py-2 px-3 text-xs uppercase font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const categoryOrder = ['Plywood', 'Blockboard', 'Doors', 'WPC/PVC', 'Laminates', 'Veneers', 'Highlighters', 'MDF', 'Hardware', 'Screws/Patti', 'Adhesives', 'ACP', 'Others'];
                  const sections = quotation.sections || [];
                  const hasSections = sections.length > 0 && quotation.items?.some(item => item.section);
                  
                  let globalIdx = 0;
                  // ColSpan grows by 1 when the SPL DISC
                  // column is present so category/section header rows
                  // still span the full table width.
                  const colSpan = 8 + (hasAnyDiscount ? 1 : 0) + (hasAnySpecialDiscount ? 1 : 0);
                  
                  if (hasSections) {
                    // Group by Section first, then by Category
                    // Move General to last
                    const sectionsOrder = sections.filter(s => s.toLowerCase() !== 'general');
                    const hasGeneralItems = quotation.items?.some(item => !item.section || item.section === 'General');
                    if (sections.includes('General') || hasGeneralItems) {
                      sectionsOrder.push('General');
                    }
                    
                    return sectionsOrder.map(sectionName => {
                      const sectionItems = sectionName === 'General'
                        ? quotation.items?.filter(item => !item.section || item.section === 'General')
                        : quotation.items?.filter(item => item.section === sectionName);
                      
                      if (!sectionItems || sectionItems.length === 0) return null;
                      
                      // Group by category within section
                      const grouped = {};
                      sectionItems.forEach(item => {
                        const cat = item.main_category || 'Others';
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push(item);
                      });
                      
                      const sortedCategories = Object.keys(grouped).sort((a, b) => {
                        const aIdx = categoryOrder.indexOf(a);
                        const bIdx = categoryOrder.indexOf(b);
                        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
                      });
                      
                      return (
                        <React.Fragment key={sectionName}>
                          {/* Section Header - gold tint, centred */}
                          <tr className="border-t border-heda-line bg-heda-gold-tint">
                            <td colSpan={colSpan} className="py-2 px-3 text-sm font-bold uppercase tracking-wide text-heda-gold text-center">
                              {sectionName.toUpperCase()}
                            </td>
                          </tr>
                          {sortedCategories.map(category => {
                            // For Plywood, Blockboard, MDF, Doors, WPC/PVC - group by brand_description
                            const needsBrandGrouping = ['Plywood', 'Blockboard', 'MDF', 'Doors', 'WPC/PVC'].includes(category);
                            
                            if (needsBrandGrouping) {
                              // Group items by brand_description
                              const brandGroups = {};
                              grouped[category].forEach(item => {
                                const key = item.brand_description || item.brand || '_no_brand_';
                                if (!brandGroups[key]) brandGroups[key] = [];
                                brandGroups[key].push(item);
                              });
                              
                              return (
                                <React.Fragment key={`${sectionName}-${category}`}>
                                  {/* Category Header */}
                                  <tr className="border-t border-heda-line bg-heda-cream/60">
                                    <td colSpan={colSpan} className="py-2 px-3 text-xs font-bold uppercase tracking-wide text-heda-sub">
                                      {category === 'Hardware' ? 'NAILS / SCREWS / HARDWARE' : category.toUpperCase()}
                                    </td>
                                  </tr>
                                  {/* Items grouped by brand_description */}
                                  {Object.entries(brandGroups).map(([brandKey, brandItems]) => (
                                    <React.Fragment key={`${sectionName}-${category}-${brandKey}`}>
                                      {/* Brand sub-header row — matches PDF behaviour:
                                          - Prefer brand_description if set (full marketing line)
                                          - Else fall back to the brand name itself ("CENTURY")
                                          - Skip only when there's truly no brand info */}
                                      {brandKey !== '_no_brand_' && (brandItems[0]?.brand_description || brandItems[0]?.brand) && (
                                        <tr className="border-t border-heda-line">
                                          <td colSpan={colSpan} className="py-2 px-3 text-xs font-bold text-center text-heda-sub">
                                            {(brandItems[0].brand_description || brandItems[0].brand).toUpperCase()}
                                          </td>
                                        </tr>
                                      )}
                                      {/* Items in this brand group */}
                                      {brandItems.map((item) => {
                                        globalIdx++;
                                        return renderItemRow(item, globalIdx, 'brand');
                                      })}
                                    </React.Fragment>
                                  ))}
                                </React.Fragment>
                              );
                            }
                            
                            // Original logic for other categories
                            return (
                              <React.Fragment key={`${sectionName}-${category}`}>
                                {/* Category Header */}
                                <tr className="border-t border-heda-line bg-heda-cream/60">
                                  <td colSpan={colSpan} className="py-2 px-3 text-xs font-bold uppercase tracking-wide text-heda-sub">
                                    {category === 'Hardware' ? 'NAILS / SCREWS / HARDWARE' : category.toUpperCase()}
                                  </td>
                                </tr>
                                {/* Items in this category */}
                                {grouped[category].map((item) => {
                                  globalIdx++;
                                  return renderItemRow(item, globalIdx, 'plain');
                                })}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    });
                  } else {
                    // Original category-only grouping (no sections)
                    const grouped = {};
                    quotation.items?.forEach(item => {
                      const cat = item.main_category || 'Others';
                      if (!grouped[cat]) grouped[cat] = [];
                      grouped[cat].push(item);
                    });
                    
                    const sortedCategories = Object.keys(grouped).sort((a, b) => {
                      const aIdx = categoryOrder.indexOf(a);
                      const bIdx = categoryOrder.indexOf(b);
                      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
                    });
                    
                    return sortedCategories.map(category => {
                      // For Plywood, Blockboard, MDF, Doors, WPC/PVC - group by brand_description
                      const needsBrandGrouping = ['Plywood', 'Blockboard', 'MDF', 'Doors', 'WPC/PVC'].includes(category);
                      
                      if (needsBrandGrouping) {
                        // Group items by brand_description
                        const brandGroups = {};
                        grouped[category].forEach(item => {
                          const key = item.brand_description || item.brand || '_no_brand_';
                          if (!brandGroups[key]) brandGroups[key] = [];
                          brandGroups[key].push(item);
                        });
                        
                        return (
                          <React.Fragment key={category}>
                            {/* Category Header */}
                            <tr className="border-t border-heda-line bg-heda-cream/60">
                              <td colSpan={colSpan} className="py-2 px-3 text-xs font-bold uppercase tracking-wide text-heda-sub">
                                {category === 'Hardware' ? 'NAILS / SCREWS / HARDWARE' : category.toUpperCase()}
                              </td>
                            </tr>
                            {/* Items grouped by brand_description */}
                            {Object.entries(brandGroups).map(([brandKey, brandItems]) => (
                              <React.Fragment key={`${category}-${brandKey}`}>
                                {/* Brand sub-header row — matches PDF behaviour:
                                    prefer brand_description, else fall back to brand name. */}
                                {brandKey !== '_no_brand_' && (brandItems[0]?.brand_description || brandItems[0]?.brand) && (
                                  <tr className="bg-white">
                                    <td colSpan={colSpan} className="py-2 px-3 text-xs font-bold text-center text-heda-sub border-b border-heda-line">
                                      {(brandItems[0].brand_description || brandItems[0].brand).toUpperCase()}
                                    </td>
                                  </tr>
                                )}
                                {/* Items in this brand group */}
                                {brandItems.map((item) => {
                                  globalIdx++;
                                  return renderItemRow(item, globalIdx, 'brand');
                                })}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        );
                      }
                      
                      // Original logic for other categories
                      return (
                        <React.Fragment key={category}>
                          {/* Category Header */}
                          <tr className="border-t border-heda-line bg-heda-cream/60">
                            <td colSpan={colSpan} className="py-2 px-3 text-xs font-bold uppercase tracking-wide text-heda-sub">
                              {category === 'Hardware' ? 'NAILS / SCREWS / HARDWARE' : category.toUpperCase()}
                            </td>
                          </tr>
                          {/* Items in this category */}
                          {grouped[category].map((item) => {
                            globalIdx++;
                            return renderItemRow(item, globalIdx, 'plain');
                          })}
                        </React.Fragment>
                      );
                    });
                  }
                })()}
              </tbody>
            </table>
          );
        })()}
        </div>

        <div className="p-4 bg-heda-cream/60 border-t border-heda-line">
          {(quotation.overall_discount_amount > 0 || (quotation.extra_charges?.length || 0) > 0) && (
            <>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-heda-mute">Subtotal</span>
                <span className="font-mono-data">{formatINR(roundINR(quotation.subtotal))}</span>
              </div>
              {quotation.overall_discount_amount > 0 && (
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-heda-mute">
                    {specialDiscountLabel(quotation.overall_discount_type, quotation.overall_discount_value)}
                  </span>
                  <span className="font-mono-data text-red-700">-{formatINR(roundINR(quotation.overall_discount_amount))}</span>
                </div>
              )}
              {(quotation.extra_charges || []).map((ec, i) => (
                <div key={ec.id || ec.label || `ec-${i}`} className="flex justify-between mb-1" data-testid={`detail-extra-${i}`}>
                  <span className="text-sm text-sky-700 font-medium">{ec.label}</span>
                  <span className="font-mono-data text-sky-700">+{formatINR(roundINR(ec.amount || 0))}</span>
                </div>
              ))}
              <Separator className="my-2 bg-heda-line" />
            </>
          )}
          <div className="flex justify-between">
            <span className="text-lg font-bold">Grand Total</span>
            <span className="text-xl font-bold font-mono-data text-heda-olive-dark" data-testid="quotation-grand-total">{formatINR(roundINR(quotation.grand_total))}</span>
          </div>
        </div>
      </div>

      {/* Delete Comparison Dialog — renders one button per
          member (2 or 3) so the user picks the survivor from any
          group size. Legacy pair still shows exactly two buttons. */}
      <Dialog open={showDeleteComparisonDialog} onOpenChange={setShowDeleteComparisonDialog}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl sm:max-w-md" data-testid="delete-comparison-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Delete Comparison
            </DialogTitle>
            <DialogDescription className="text-heda-sub">
              This will delete the comparison and keep only one option. The remaining quotation will become a normal quotation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-heda-ink">Which option do you want to keep?</p>
            <div className="flex flex-col gap-2">
              {(() => {
                const members = groupMembers && groupMembers.length > 0
                  ? groupMembers
                  // Legacy fallback — the group query hadn't returned yet.
                  // Render the canonical 2-button dialog with the
                  // pre-iter-131 semantics ("option1"/"option2" strings).
                  : [
                    { id: 'option1', option_number: 1, quotation_number: '', grand_total: null, _legacy: true },
                    { id: 'option2', option_number: 2, quotation_number: '', grand_total: null, _legacy: true },
                  ];
                return members.map((m) => {
                  const others = members.filter(x => x.id !== m.id);
                  const otherLabel = others.length === 1
                    ? `Delete Option ${others[0].option_number}`
                    : `Delete Options ${others.map(x => x.option_number).join(' & ')}`;
                  return (
                    <Button
                      key={m.id}
                      onClick={() => handleDeleteComparison(m._legacy ? m.id : m.id)}
                      disabled={deletingComparison}
                      variant="outline"
                      className="rounded-xl w-full h-12 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold justify-start px-4"
                      data-testid={`keep-option-${m.option_number}-btn`}
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-semibold">
                          Keep Option {m.option_number}
                          {m.quotation_number ? ` · ${m.quotation_number}` : ''}
                        </span>
                        <span className="text-xs text-heda-mute">{otherLabel}</span>
                      </div>
                    </Button>
                  );
                });
              })()}
            </div>
            <Button
              onClick={() => setShowDeleteComparisonDialog(false)}
              variant="ghost"
              className="rounded-xl w-full text-heda-sub"
              disabled={deletingComparison}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
